<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { recommendCommonConfig } from './configs/common'
import RecommendPageDefault from './components/RecommendPage.default.vue'
import RecommendPageFlipnovel from './components/RecommendPage.flipnovel.vue'
import RecommendPageNovelyn from './components/RecommendPage.novelyn.vue'
import RecommendPageNovelmello from './components/RecommendPage.novelmello.vue'
import { api } from '@/api'
import { useFinishedClick, useFinishedView } from '@/composables/tracks'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'
import { STORAGE_CUSTOM_THEME } from '@/constants'
import { loadBrandComponent } from '@/utils/loadBrandConfig'

const props = defineProps<{
  contentId: string
}>()

// 读取章节阅读器的主题设置
const chapterTheme = useLocalStorage(STORAGE_CUSTOM_THEME, {
  background: '#F6F7F9',
  size: '38',
  skinId: '',
})

const config = recommendCommonConfig

const RecommendPage = loadBrandComponent({
  rocnovel: RecommendPageDefault,
  oceread: RecommendPageDefault,
  talewave: RecommendPageDefault,
  ficmoon: RecommendPageDefault,
  novelarc: RecommendPageDefault,
  storyella: RecommendPageDefault,
  aurenovel: RecommendPageDefault,
  silktale: RecommendPageDefault,
  pearla: RecommendPageDefault,
  fictora: RecommendPageDefault,
  marinovel: RecommendPageDefault,
  flipnovel: RecommendPageFlipnovel,
  novelyn: RecommendPageNovelyn,
  // NovelMello 完读页：固定浅底 #FBF6FB，不跟随阅读器主题色（走 flipnovel 那条路线的独立皮肤）
  novelmello: RecommendPageNovelmello,
})

const allData = ref<any>({})
const recommendations = ref<any>([])
const fetching = ref(false)
const error = ref<any>()
const pageSize = 10
const total = ref(0)

const route = useRoute()
const { finishedView } = useFinishedView()
const { finishedClick } = useFinishedClick()

const statusDisplay = computed(() => {
  return route.query.status === 'finish' ? config.texts.end : config.texts.ongoing
})

const statusDescription = computed(() => {
  return route.query.status === 'finish'
    ? config.texts.endDescription
    : config.texts.ongoingDescription
})

async function fetchRecommendations() {
  fetching.value = true
  try {
    const result = await api.get<any, GetContentGetfinishlikecolumnResponse>('/content/getFinishLikeColumn', {
      params: {
        contentId: props.contentId,
        pageIndex: 1,
        pageSize,
      },
    })

    const data = result.data
    allData.value = data
    recommendations.value = data?.resourceList?.records || []
    total.value = data?.resourceList?.total || 0

    // 曝光埋点
    finishedView({
      contentId: props.contentId,
      serialStatus: route.query.status === 'finish' ? 'completed' : 'ongoing',
      showNum: recommendations.value.length,
      title: 'finished',
    })
  }
  catch (_error) {
    error.value = _error
  }
  finally {
    fetching.value = false
  }
}

const router = useRouter()

function goToCollection() {
  // More 按钮点击埋点
  finishedClick({
    elementName: 'more',
    contentId: props.contentId,
    serialStatus: route.query.status === 'finish' ? 'completed' : 'ongoing',
    showNum: recommendations.value.length,
    title: 'finished',
  })

  router.push({
    name: 'collection',
    params: {
      columnId: props.contentId, // 完读推荐只需要contentId
    },
    query: {
      type: 'finish',
      status: route.query.status as string, // 传递 status 参数
    },
  })
}

function handleBookClick(book: any) {
  // 书籍点击埋点
  finishedClick({
    elementName: 'bookclick',
    contentId: props.contentId,
    serialStatus: route.query.status === 'finish' ? 'completed' : 'ongoing',
    showNum: recommendations.value.length,
    title: 'finished',
    contentIdRecbook: book.id,
  })

  // 导航到书籍详情页
  router.push({ name: READER_ROUTE_NAME, params: { contentId: book.id, chapterId: book.firstChapter!.id } })
}

onMounted(() => {
  fetchRecommendations()
})
</script>

<template>
  <RecommendPage
    :config="config"
    :chapter-theme="chapterTheme"
    :status-display="statusDisplay"
    :status-description="statusDescription"
    :fetching="fetching"
    :error="error"
    :recommendations="recommendations"
    :all-data="allData"
    :total="total"
    :page-size="pageSize"
    :go-to-collection="goToCollection"
    :handle-book-click="handleBookClick"
  />
</template>
